import { useEffect } from "react";

export default function useOnClickOutside(
  ref: HTMLDivElement | null,
  handler: (e?: MouseEvent | TouchEvent) => void,
  /**
   * A second element also treated as "inside" — e.g. the popup when it's
   * portaled elsewhere in the DOM and so isn't a descendant of `ref`
   * anymore. A dedicated param (not an array) keeps both refs's identities
   * stable across renders, so this effect doesn't re-subscribe every render.
   */
  extraRef?: HTMLDivElement | null,
) {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      // A null `ref` disables the check entirely (matches the pre-existing
      // single-ref behaviour) — not treated as "no primary element, so
      // anything is outside".
      if (
        !ref ||
        ref.contains(target) ||
        (extraRef && extraRef.contains(target))
      ) {
        return;
      }

      handler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, extraRef, handler]);
}
